import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ADMIN_DEFAULT_PAGE_SIZE, clampPage, parsePageParam } from '@/lib/pagination';

type UseAdminTablePaginationParams = {
  /** Current 0-based page, parsed by the caller from the page param. */
  page: number;
  /** Current rows-per-page, parsed by the caller from the size param. */
  pageSize: number;
  /** Total rows matching the ACTIVE filters — drives the post-result clamp. */
  total: number;
  /** True only when the last fetch succeeded — clamping waits for real data. */
  isSuccess: boolean;
  /**
   * Serialized active filters (status, debounced search, …). Any change
   * folds the table back to page 1.
   */
  filterKey: string;
  /**
   * URL param names. Single-table pages keep the Spec defaults
   * (`page`/`pageSize`); pages hosting several paginated tables namespace
   * them (`queuePage`/`queuePageSize`, `logsPage`/`logsPageSize` —
   * ADR-056 §2) so each table deep-links independently.
   */
  pageParam?: string;
  pageSizeParam?: string;
};

type UseAdminTablePaginationResult = {
  goPage: (page: number) => void;
  setPageSize: (size: number) => void;
};

// Canonical URL form: page 1 and the default size are ABSENT (`/admin/deals`,
// not `/admin/deals?page=1&pageSize=50`) — deep links stay minimal and the
// "did anything change" comparisons below stay string-exact.
function writePage(sp: URLSearchParams, param: string, page: number): void {
  if (page <= 0) sp.delete(param);
  else sp.set(param, String(page + 1));
}

function writePageSize(sp: URLSearchParams, param: string, size: number): void {
  if (size === ADMIN_DEFAULT_PAGE_SIZE) sp.delete(param);
  else sp.set(param, String(size));
}

/**
 * URL-driven pagination contract for admin tables —
 * UI_Behavior §4.10 L661-667 + ADR-056.
 *
 * The page reads page/pageSize straight from `useSearchParams` on every
 * render (the URL is the source of truth, so Back/Forward replays states
 * natively); this hook owns the writes:
 *
 * - voluntary navigation (page click, size change) → `push`;
 * - automatic resets (filter change, stale-page clamp, malformed-param
 *   canonicalization) → `replace`, so history never records transient or
 *   invalid states;
 * - combined changes (size → page 1) land in ONE write — never a push
 *   followed by a replace;
 * - no-op writes are skipped entirely (react-router pushes even for an
 *   identical URL, which would pollute Back/Forward history).
 */
export function useAdminTablePagination({
  page,
  pageSize,
  total,
  isSuccess,
  filterKey,
  pageParam = 'page',
  pageSizeParam = 'pageSize',
}: UseAdminTablePaginationParams): UseAdminTablePaginationResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const goPage = (next: number) => {
    // No-op guard: react-router's setSearchParams pushes even for an
    // identical URL, which would pollute Back/Forward history with
    // duplicate entries (e.g. clicking the active page number).
    const sp = new URLSearchParams(searchParams);
    writePage(sp, pageParam, next);
    if (sp.toString() === searchParams.toString()) return;
    setSearchParams(sp, { replace: false });
  };

  // Changing the size re-anchors to page 1 in the same atomic write — the
  // old offset would point at arbitrary rows under the new window.
  const setPageSize = (size: number) => {
    const sp = new URLSearchParams(searchParams);
    sp.delete(pageParam);
    writePageSize(sp, pageSizeParam, size);
    if (sp.toString() === searchParams.toString()) return;
    setSearchParams(sp, { replace: false });
  };

  // One-shot canonicalization of malformed raw params (`?page=abc`, `0`,
  // `-3`, `1.5`, `pageSize=37`) — parsing already normalized the VALUES;
  // this folds the URL itself so the junk doesn't survive in history or
  // get copy-pasted onward. Replace: nothing the user did.
  const didNormalize = useRef(false);
  useEffect(() => {
    if (didNormalize.current) return;
    didNormalize.current = true;
    const canonical = new URLSearchParams(searchParams);
    writePage(canonical, pageParam, page);
    writePageSize(canonical, pageSizeParam, pageSize);
    if (canonical.toString() !== searchParams.toString()) {
      setSearchParams(canonical, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter/search/size change → back to page 1. The result set changed, so
  // the old offset points at arbitrary rows. Skip the very first run —
  // resetting there would clobber a deep-linked page param.
  const isFirstFilterEffect = useRef(true);
  useEffect(() => {
    if (isFirstFilterEffect.current) {
      isFirstFilterEffect.current = false;
      return;
    }
    if (parsePageParam(searchParams.get(pageParam)) === 0) return;
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        sp.delete(pageParam);
        return sp;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // Post-result clamp — parsing never knew totalPages; once the exact count
  // lands, fold an overshooting page param (stale deep link, shrunk result
  // set, deletion on the last page) onto the last real page.
  useEffect(() => {
    if (!isSuccess) return;
    const clamped = clampPage(page, total, pageSize);
    if (clamped === page) return;
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        writePage(sp, pageParam, clamped);
        return sp;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, total, page, pageSize]);

  return { goPage, setPageSize };
}
